import React from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import { Empty, Statistic, Row, Col } from 'antd';
import './TimeseriesWidget.scss';

const TimeseriesWidget = ({ data, config }) => {
  if (!data || !data.datasets || data.datasets.length === 0) {
    return <Empty description="No hay datos disponibles" />;
  }

  // Formatear número para tooltip
  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return Number(num).toLocaleString('es-ES', { maximumFractionDigits: 2 });
  };

  // Configurar opciones de ApexCharts
  const chartOptions = {
    chart: {
      type: 'area',
      height: '100%',
      toolbar: { 
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true
        }
      },
      zoom: {
        enabled: true,
        type: 'x',
        autoScaleYaxis: true
      },
      animations: {
        enabled: true,
        speed: 800
      }
    },
    colors: config.colores || data.datasets.map((ds, idx) => 
      ds.borderColor || ['#1890ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1'][idx % 5]
    ),
    dataLabels: { 
      enabled: false 
    },
    stroke: {
      curve: 'smooth',
      width: 2
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.1,
        stops: [0, 90, 100]
      }
    },
    xaxis: {
      type: 'datetime',
      labels: {
        format: 'dd/MM HH:mm',
        rotate: -45,
        rotateAlways: false,
        hideOverlappingLabels: true,
        style: {
          fontSize: '11px'
        }
      },
      tooltip: {
        enabled: false
      }
    },
    yaxis: {
      labels: {
        formatter: (val) => formatNumber(val),
        style: {
          fontSize: '11px'
        }
      }
    },
    tooltip: {
      shared: true,
      intersect: false,
      theme: 'light',
      style: {
        fontSize: '12px'
      },
      x: {
        format: 'dd/MM/yyyy HH:mm'
      },
      y: {
        formatter: (val, { seriesIndex }) => {
          const dataset = data.datasets[seriesIndex];
          
          // Si es sensor de consumo, mostrar "Consumo:"
          if (dataset?.esAcumulativo) {
            return `Consumo: ${formatNumber(val)} ${dataset.unidades || ''}`;
          }
          
          // Si es sensor normal, mostrar valor
          return `${formatNumber(val)} ${dataset.unidades || ''}`;
        },
        title: {
          formatter: (seriesName, opts) => {
            const dataset = data.datasets[opts.seriesIndex];
            // El label ya incluye el tipo si es necesario
            return dataset?.label || seriesName;
          }
        }
      },
      custom: function({ series, seriesIndex, dataPointIndex, w }) {
        const dataset = data.datasets[seriesIndex];
        const valor = series[seriesIndex][dataPointIndex];
        
        // Obtener la fecha del punto de datos
        const timestamp = w.globals.seriesX[seriesIndex][dataPointIndex];
        const fecha = new Date(timestamp);
        const fechaFormateada = fecha.toLocaleString('es-MX', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
        
        // Para sensores NO de consumo, mostrar min/max/promedio si están disponibles
        if (!dataset?.esAcumulativo && dataset?.datosDetallados && dataset.datosDetallados[dataPointIndex]) {
          const detalles = dataset.datosDetallados[dataPointIndex];
          return `
            <div class="apexcharts-tooltip-custom">
              <div class="tooltip-fecha">${fechaFormateada}</div>
              <div class="tooltip-title">${dataset.label}</div>
              <div class="tooltip-value"><strong>Promedio:</strong> ${formatNumber(detalles.promedio)} ${dataset.unidades}</div>
              <div class="tooltip-value"><strong>Mínimo:</strong> ${formatNumber(detalles.minimo)} ${dataset.unidades}</div>
              <div class="tooltip-value"><strong>Máximo:</strong> ${formatNumber(detalles.maximo)} ${dataset.unidades}</div>
            </div>
          `;
        }
        
        // Para sensores de consumo o sin detalles
        const prefijo = dataset?.esAcumulativo ? 'Consumo: ' : '';
        return `
          <div class="apexcharts-tooltip-custom">
            <div class="tooltip-fecha">${fechaFormateada}</div>
            <div class="tooltip-title">${dataset.label}</div>
            <div class="tooltip-value">${prefijo}${formatNumber(valor)} ${dataset.unidades || ''}</div>
          </div>
        `;
      }
    },
    legend: {
      show: config.mostrarLeyenda !== false,
      position: 'top',
      horizontalAlign: 'left',
      fontSize: '12px',
      markers: {
        width: 10,
        height: 10,
        radius: 2
      }
    },
    grid: {
      show: true,
      borderColor: '#f0f0f0',
      strokeDashArray: 3,
      xaxis: {
        lines: { show: false }
      },
      yaxis: {
        lines: { show: true }
      },
      padding: {
        top: 0,
        right: 10,
        bottom: 0,
        left: 10
      }
    }
  };

  // Convertir datos de Chart.js a ApexCharts
  const chartSeries = data.datasets.map((dataset) => ({
    name: dataset.label || 'Serie',
    data: data.labels.map((label, index) => ({
      x: new Date(label).getTime(),
      y: dataset.data[index]
    }))
  }));

  return (
    <div className="timeseries-widget">
      {/* Estadísticas por dataset */}
      {data.datasets && data.datasets.length > 0 && (
        <div className="datasets-stats">
          {data.datasets.map((dataset, idx) => (
            <div key={idx} className="dataset-stat-row">
              <div className="dataset-name">{dataset.label}</div>
              <Row gutter={[8, 8]} className="widget-stats">
                {dataset.estadisticas?.promedio !== undefined && (
                  <Col xs={8}>
                    <Statistic
                      title="Promedio"
                      value={dataset.estadisticas.promedio}
                      suffix={dataset.unidades}
                      precision={2}
                      valueStyle={{ fontSize: 'clamp(10px, 1.8vw, 13px)' }}
                    />
                  </Col>
                )}
                {dataset.estadisticas?.min !== undefined && (
                  <Col xs={8}>
                    <Statistic
                      title="Mínimo"
                      value={dataset.estadisticas.min}
                      suffix={dataset.unidades}
                      precision={2}
                      valueStyle={{ fontSize: 'clamp(10px, 1.8vw, 13px)', color: '#52c41a' }}
                    />
                  </Col>
                )}
                {dataset.estadisticas?.max !== undefined && (
                  <Col xs={8}>
                    <Statistic
                      title="Máximo"
                      value={dataset.estadisticas.max}
                      suffix={dataset.unidades}
                      precision={2}
                      valueStyle={{ fontSize: 'clamp(10px, 1.8vw, 13px)', color: '#f5222d' }}
                    />
                  </Col>
                )}
                {/* Consumo Total para sensores acumulativos */}
                {dataset.esAcumulativo && dataset.estadisticas?.consumoTotal !== undefined && (
                  <Col xs={24}>
                    <Statistic
                      title="Consumo Total del Período"
                      value={dataset.estadisticas.consumoTotal}
                      suffix={dataset.unidades}
                      precision={2}
                      valueStyle={{ 
                        fontSize: 'clamp(12px, 2vw, 15px)', 
                        color: '#1890ff',
                        fontWeight: 'bold'
                      }}
                    />
                  </Col>
                )}
              </Row>
            </div>
          ))}
        </div>
      )}
      
      <div className="chart-container">
        <Chart
          options={chartOptions}
          series={chartSeries}
          type="area"
          height="100%"
        />
      </div>

      {/* Información adicional según tipo de sensor */}
      {data.datasets?.[0]?.esAcumulativo && (
        <div className="widget-footer-info">
          <small>
            ℹ️ Muestra consumo diferencial por intervalo
          </small>
        </div>
      )}
      
      {data.estadisticas?.intervalo && (
        <div className="widget-footer-info">
          <small>
            Agrupado por: <strong>{data.estadisticas.intervalo.unit === 'hour' ? 'Hora' : 'Día'}</strong>
          </small>
        </div>
      )}
    </div>
  );
};

TimeseriesWidget.propTypes = {
  data: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.string).isRequired,
    datasets: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        data: PropTypes.arrayOf(PropTypes.number),
        borderColor: PropTypes.string,
        esAcumulativo: PropTypes.bool, // Indica si es sensor de consumo
        unidades: PropTypes.string,
        estadisticas: PropTypes.shape({
          min: PropTypes.number,
          max: PropTypes.number,
          promedio: PropTypes.number,
          consumoTotal: PropTypes.number, // Solo para sensores acumulativos
        }),
        datosDetallados: PropTypes.arrayOf(
          PropTypes.shape({
            promedio: PropTypes.number,
            minimo: PropTypes.number,
            maximo: PropTypes.number,
          })
        ), // Solo para sensores NO acumulativos
      })
    ).isRequired,
    estadisticas: PropTypes.shape({
      intervalo: PropTypes.shape({
        unit: PropTypes.string, // "hour" o "day"
        binSize: PropTypes.number,
      }),
    }),
  }).isRequired,
  config: PropTypes.shape({
    mostrarLeyenda: PropTypes.bool,
    colores: PropTypes.arrayOf(PropTypes.string),
    mostrarEstadisticas: PropTypes.bool,
  }).isRequired,
};

export default TimeseriesWidget;
